import type { TileSet } from './tiles';
import { pairs, type WorldMapSpec } from './worldSpec';

/** Neon Heights' painting (approved 29 October), tiled as
 *  public/world/neon/v1 at WebP Q80. A changed painting goes in a new
 *  version folder, with its spots measured again. */
export const NEON_TILES: TileSet = {
  base: '/world/neon/v1/map_files',
  width: 3072,
  height: 2048,
  tileSize: 512,
  top: 3,
};

/**
 * Neon Heights, from the painting's spots.json: a city district at night, its
 * seven buildings standing among blocks, gardens and a wet street (node ids
 * 29 to 35, the world's slots in order). The painting is dense, so every plate
 * hangs where the painted blocks and gardens leave room beside or above its
 * building, clear of every outline, the painted lamps and the pieces' places
 * (Jon, 26 October: beside or above, never painted for): the four buildings
 * that stand close to the road (Neon Reels, Synthwave, the Speedway and Glitch)
 * never hang below. Where a building centred on a phone leaves its plate's spot just off screen (the painting is
 * dense, so no honest spot is nearer), the scene pulls the plate onto the screen (worlds.ts `pullWorldPlate`); the
 * Synthwave's tight spot hangs above its top right, clear of the crown button at 375 wide.
 *
 * The 16 street lamps are painted, unlit (Jon, 28 October, plan question 7:
 * so the road's lanterns can show a player's progress), and stand across the
 * whole district, on its pavements and plazas, not only along the main road:
 * the code draws only a glow at each (`lanterns`, from the painting's
 * lamps.json), lit in order along the road.
 */
export const NEON: WorldMapSpec = {
  biome: 'neon',
  tiles: NEON_TILES,
  buildings: [
    {
      node: 29,
      name: 'Neon Reels',
      centre: [380, 510],
      plate: [820, 225],
      plateTight: [820, 205],
      plateNarrow: true,
      outline: pairs([
        70, 320, 140, 300, 200, 280, 236, 240, 290, 180, 340, 140, 380, 110, 430, 140, 444, 200,
        480, 230, 540, 250, 610, 340, 650, 400, 690, 450, 690, 600, 640, 680, 600, 690, 560, 750,
        500, 750, 470, 790, 300, 790, 270, 750, 200, 730, 130, 660, 124, 580, 70, 520,
      ]),
    },
    {
      node: 30,
      name: 'Arcade',
      centre: [770, 1180],
      plate: [280, 965],
      plateTight: [280, 965],
      plateNarrow: true,
      outline: pairs([
        800, 894, 840, 940, 900, 980, 940, 1040, 950, 1080, 1000, 1110, 1016, 1160, 1014, 1300, 960,
        1380, 880, 1420, 800, 1450, 700, 1454, 620, 1400, 570, 1380, 540, 1280, 524, 1200, 500,
        1140, 486, 1090, 510, 1050, 570, 1040, 600, 1000, 680, 980, 740, 940, 790, 910,
      ]),
    },
    {
      node: 31,
      name: 'Synthwave',
      centre: [1250, 530],
      plate: [1735, 205],
      plateTight: [1565, 115],
      plateNarrow: true,
      outline: pairs([
        994, 470, 994, 600, 1000, 690, 1040, 770, 1120, 790, 1200, 790, 1300, 770, 1380, 760, 1420,
        720, 1500, 640, 1504, 520, 1540, 400, 1570, 360, 1490, 300, 1400, 280, 1280, 236, 1240, 210,
        1200, 230, 1090, 300, 1040, 370, 994, 430,
      ]),
    },
    {
      node: 32,
      name: 'Cyber Heist',
      centre: [1560, 1280],
      plate: [2040, 1385],
      plateTight: [2040, 1385],
      plateNarrow: true,
      outline: pairs([
        1400, 990, 1480, 940, 1580, 930, 1650, 980, 1656, 1040, 1700, 1080, 1704, 1150, 1756, 1180,
        1824, 1200, 1826, 1490, 1760, 1530, 1700, 1560, 1600, 1580, 1580, 1620, 1460, 1620, 1430,
        1580, 1380, 1540, 1310, 1490, 1286, 1400, 1286, 1080, 1320, 1040, 1380, 1000,
      ]),
    },
    {
      node: 33,
      name: 'Speedway',
      centre: [1970, 740],
      plate: [2165, 205],
      plateTight: [2190, 205],
      plateNarrow: true,
      outline: pairs([
        1664, 700, 1672, 640, 1720, 580, 1800, 530, 1900, 500, 2000, 450, 2070, 400, 2084, 344,
        2120, 344, 2160, 430, 2184, 470, 2220, 430, 2260, 450, 2270, 510, 2240, 570, 2270, 600,
        2280, 680, 2290, 760, 2280, 840, 2240, 880, 2180, 910, 2120, 950, 2040, 980, 1940, 980,
        1860, 990, 1760, 940, 1700, 910, 1660, 880, 1660, 760,
      ]),
    },
    {
      node: 34,
      name: 'Skyline',
      centre: [2570, 640],
      plate: [2080, 1010],
      plateTight: [2097, 1010],
      plateNarrow: true,
      outline: pairs([
        2562, 24, 2586, 24, 2592, 120, 2670, 160, 2724, 250, 2750, 340, 2776, 400, 2770, 500, 2772,
        660, 2776, 700, 2772, 800, 2776, 860, 2816, 890, 2820, 972, 2770, 994, 2712, 1056, 2648,
        1104, 2580, 1168, 2490, 1184, 2410, 1176, 2350, 1120, 2300, 1050, 2296, 910, 2370, 860,
        2368, 400, 2392, 350, 2420, 260, 2444, 200, 2500, 140, 2550, 110,
      ]),
    },
    {
      node: 35,
      name: 'Glitch',
      centre: [2810, 1380],
      plate: [2380, 1590],
      plateTight: [2380, 1560],
      plateNarrow: true,
      outline: pairs([
        2680, 1110, 2800, 1024, 2820, 1030, 2850, 1100, 2910, 1110, 2960, 1180, 3000, 1200, 2990,
        1300, 3010, 1400, 3030, 1480, 3040, 1580, 3020, 1680, 2940, 1690, 2900, 1700, 2840, 1700,
        2760, 1700, 2720, 1690, 2670, 1660, 2610, 1620, 2580, 1560, 2584, 1480, 2580, 1400, 2620,
        1360, 2640, 1280, 2660, 1220, 2640, 1160,
      ]),
    },
  ],
  road: pairs([
    0, 860, 200, 894, 400, 904, 600, 880, 800, 844, 940, 840, 1040, 876, 1160, 916, 1320, 936, 1480,
    940, 1600, 970, 1720, 1020, 1840, 1090, 1960, 1150, 2080, 1192, 2200, 1230, 2320, 1280, 2440,
    1344, 2510, 1400, 2500, 1490, 2480, 1600, 2470, 1700, 2510, 1770, 2700, 1824, 2900, 1840, 3072,
    1836,
  ]),
  roadWidth: 90,
  still: [],
  // The painting's 16 street lamps (lamps.json, the lantern head's centre, left
  // to right in the painting's reading order), unlit: the code draws only a
  // glow at each, and the lanterns' order along the road is the order they
  // light in. They stand beside the lower street and round the plazas too, so
  // some are far from the main road's centre line (every one is within the road's
  // width of it or within 160 of a building's outline).
  lanterns: pairs([
    150, 660, 398, 1206, 536, 760, 810, 700, 1010, 690, 1044, 1340, 1326, 1504, 1414, 770, 1590,
    810, 1672, 1596, 1884, 976, 2146, 904, 2340, 594, 2488, 1536, 2552, 1216, 2896, 1696,
  ]),
  plateBox: [420, 110],
  plateBoxTight: [528, 138],
  plateBoxNarrow: [394, 138],
  ambient: '/world/neon/ambient/v3',
  ambientFiles: ['life.json', 'life.webp', 'sway.webp'],
  // Neon Heights' road friends' kinds: no cats, robots, flamingos (nor neon
  // flamingo signs), living spotlights, top-hat magicians or roller skaters in
  // its life (night walkers, street sellers, hovercars, a hover shuttle and
  // searchlight beams stand in).
  forbidden: ['cat', 'robot', 'flamingo', 'spotlight', 'magician', 'skater'],
};
